import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "../../Styles/ann-tokens.css";
import "../../Styles/marketing-base.css";
import "../../Styles/pages/first-principles-b.css";

/* ============================================================
   "The First Principles of Hiring" — long-form essay, redesign.
   Same editorial register as the original (Newsreader + IBM
   Plex, warm cream, cyan accent) — see
   Styles/pages/first-principles-b.css. Site header/footer wrap
   this like every other route; the page owns only its body
   content and the floating share affordance.
   ============================================================ */

const FIG1_CHAIN = [
  { label: "Outcome", cap: "the original", bar: "#0f9aab", labelColor: "#0b6b76", capColor: "rgba(11,107,118,.75)", glow: true },
  { label: "Job Description", cap: "requirements", bar: "rgba(27,30,34,.34)", labelColor: "rgba(27,30,34,.8)", capColor: "rgba(27,30,34,.51)" },
  { label: "Keywords", cap: "search strings", bar: "rgba(27,30,34,.25)", labelColor: "rgba(27,30,34,.7)", capColor: "rgba(27,30,34,.46)" },
  { label: "Resumes", cap: "claims", bar: "rgba(27,30,34,.17)", labelColor: "rgba(27,30,34,.6)", capColor: "rgba(27,30,34,.42)" },
  { label: "Impressions", cap: "a feeling", bar: "rgba(27,30,34,.08)", labelColor: "rgba(27,30,34,.52)", capColor: "rgba(27,30,34,.4)" },
];

const THEOREM_ROWS = [
  ["Requirements", "Success"],
  ["Responsibilities", "Outcomes"],
  ["Qualifications", "Capability"],
  ["Experience", "Evidence"],
];

const FIG2_STAGES = [
  { n: "01", label: "Search", cap: "gathers it" },
  { n: "02", label: "Resume evaluation", cap: "weighs it" },
  { n: "03", label: "Interview", cap: "tests it" },
  { n: "04", label: "Recommendation", cap: "assembles it" },
];

export default function FirstPrinciplesB() {
  const [toast, setToast] = useState("");
  const toastTimer = useRef(null);

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  function showToast(msg) {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(""), 2200);
  }

  function share() {
    const url = window.location.href;
    const data = {
      title: "The First Principles of Hiring",
      text:
        "Every hiring process is exactly as good as its definition of success. No better.",
      url,
    };
    if (navigator.share) {
      navigator.share(data).catch(() => {});
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(
        () => showToast("Link copied to clipboard"),
        () => showToast(url)
      );
    } else {
      showToast(url);
    }
  }

  return (
    <div className="fpb">
      {/* Opening */}
      <section className="fpb-opening" aria-labelledby="fpb-h1">
        <div className="fpb-col">
          <p className="fpb-eyebrow">A five-minute argument</p>
          <h1 id="fpb-h1">The First Principles of Hiring</h1>
        </div>
      </section>

      {/* Opening blockquote + the sentence */}
      <section className="fpb-quote-open">
        <div className="fpb-col">
          <blockquote className="fpb-bq">
            The room is on its fourth candidate of the afternoon. The scores
            are inconclusive. The panel is split — two in favour, two
            unsure, one quietly checking his phone. And then the decision
            arrives, in a sentence everyone in hiring knows:
          </blockquote>
          <p className="fpb-bigquote">&ldquo;I have a good feeling about her.&rdquo;</p>
          <p className="copy">Careers turn on that sentence. So do companies.</p>
          <p className="copy">
            This page is about that sentence — where it comes from, why it
            decides so much, and what it would take to retire it.
          </p>
        </div>
      </section>

      {/* I · The Wager */}
      <section className="fpb-movement" aria-labelledby="fpb-m1">
        <div className="fpb-col">
          <div className="fpb-head">
            <span className="num">I</span>
            <h2 id="fpb-m1">The Wager</h2>
          </div>
          <p className="copy">
            No one hires for the sake of hiring. A role opens because
            something must be achieved — revenue that isn&rsquo;t growing on
            its own, a function that needs an owner, a problem that will not
            solve itself. And someone decided that a new person is the right
            way to get it done.
          </p>
          <p className="pull-md">
            Before there was a vacancy, there was an outcome someone needed.
          </p>
          <p className="copy">
            Which makes every hire a prediction. Someone looked at the
            available evidence and predicted that this person, out of
            everyone considered, would deliver that outcome.
          </p>
          <p className="copy">The offer letter is the wager.</p>
          <p className="copy">So it is worth asking how that prediction usually gets made.</p>
        </div>
      </section>

      {/* II · Thirty Years of Faster */}
      <section className="fpb-movement" aria-labelledby="fpb-m2">
        <div className="fpb-col">
          <div className="fpb-head">
            <span className="num">II</span>
            <h2 id="fpb-m2">Thirty Years of Faster</h2>
          </div>
          <p className="copy">
            Over the last thirty years, nearly every part of hiring has been
            rebuilt. Job boards replaced newspaper columns. Applicant
            tracking systems replaced filing cabinets. LinkedIn made half
            the working world searchable from a desk. Now AI reads resumes
            in milliseconds and sends outreach by the thousand.
          </p>
          <p className="copy">
            Every one of these made hiring faster. Some made it wider. None
            of them fixed what happens <em>before</em> the tools switch on —
            the standard that was never defined.
          </p>
          <p className="copy">
            Better methods for judging candidates do exist. Structured
            interviews, work-sample tests, evidence-based scoring — all
            measurably better than a conversation and a hunch. But a tool
            cannot define success for you. That part was left to the
            people. And the people were given nothing to work from.
          </p>
          <p className="copy">
            Ask anyone who has hired for twenty years whether hiring feels
            more <em>certain</em> than it did in the nineties. You will get
            a wry look.
          </p>
          <p className="copy">
            Thirty years of better tools — and the decision still rests on a
            standard nobody wrote down.
          </p>
          <p className="pull-lg">
            We improved every step of the process without ever asking what
            the process was for.
          </p>
        </div>
      </section>

      {/* III · The Lost Standard */}
      <section className="fpb-movement" aria-labelledby="fpb-m3">
        <div className="fpb-col">
          <div className="fpb-head">
            <span className="num">III</span>
            <h2 id="fpb-m3">The Lost Standard</h2>
          </div>
          <p className="copy">
            Go back to the moment a role opens. At that moment, the standard
            exists. The hiring manager knows — often precisely — what the
            new person must produce. That need is the reason the role
            exists at all.
          </p>
          <p className="copy">Then the translating begins.</p>
          <p className="copy">
            The outcome becomes a job description: requirements,
            responsibilities, qualifications. The job description becomes
            search keywords. The keywords surface resumes. The resumes
            become impressions — a strong brand here, a familiar title
            there.
          </p>
          <p className="copy">
            Every translation loses something. And it always loses the same
            thing: the reason the role exists.
          </p>
        </div>
      </section>

      {/* FIG. 1 */}
      <figure className="fpb-fig1">
        <div className="fpb-col">
          <figcaption>Fig. 1 — Each translation loses the reason the role exists</figcaption>
          <div className="chain">
            {FIG1_CHAIN.map((c) => (
              <div className="col" key={c.label}>
                <div
                  className="bar"
                  style={{
                    background: c.bar,
                    boxShadow: c.glow ? "0 0 0 4px rgba(35,201,217,.14)" : "none",
                  }}
                />
                <span className="lbl" style={{ color: c.labelColor }}>{c.label}</span>
                <span className="cap" style={{ color: c.capColor }}>{c.cap}</span>
              </div>
            ))}
          </div>
          <div className="callout">
            <span className="sq" aria-hidden="true" />
            <span className="txt">Ann begins here — on the outcome, before the first translation</span>
          </div>
        </div>
      </figure>

      {/* continuation */}
      <section className="fpb-movement tight">
        <div className="fpb-col">
          <p className="copy">
            By the time a candidate sits in an interview, they are being
            measured against a photocopy of a photocopy of the outcome. And
            nobody in the room has seen the original.
          </p>
          <p className="copy">
            It is easy to miss the loss, because each instrument does
            measure <em>something</em>. Just never quite the right thing.
          </p>
        </div>
      </section>

      {/* Theorem — Requirements ≠ Success (dark) */}
      <section className="fpb-theorem">
        <div className="fpb-col">
          <div className="grid">
            {THEOREM_ROWS.map((row) => (
              <React.Fragment key={row[0]}>
                <span className="l">{row[0]}</span>
                <span className="ne">&#8800;</span>
                <span className="r">{row[1]}</span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* continuation */}
      <section className="fpb-movement tight">
        <div className="fpb-col">
          <p className="copy">
            And when the standard is missing, something predictable fills
            the gap: personal judgment. Not because people are careless —
            it is simply what judgment does when nothing else is written
            down. It is why interviews drift into chemistry tests. Why a
            famous employer on a resume outweighs an unglamorous decade of
            results. Why five interviewers reach five verdicts about the
            same person. Why bias finds room to work.
          </p>
          <p className="copy">
            Nobody wrote the standard down. So everyone in the process
            quietly invented their own.
          </p>
          <p className="copy">
            That is the room this page opened in. The good feeling was
            never the real problem. The missing standard was.
          </p>
        </div>
      </section>

      {/* Which leaves one question */}
      <section className="fpb-question">
        <div className="fpb-col">
          <p className="fpb-eyebrow">Which leaves one question</p>
          <h2>What exactly are we measuring candidates against?</h2>
        </div>
      </section>

      {/* IV · Define Success First */}
      <section className="fpb-movement" aria-labelledby="fpb-m4">
        <div className="fpb-col">
          <div className="fpb-head">
            <span className="num">IV</span>
            <h2 id="fpb-m4">Define Success First</h2>
          </div>
          <p className="copy">
            Many teams will say the standard already exists — they have
            scorecards, competency frameworks, structured interviews,
            success profiles. Good. Now read one honestly.
          </p>
          <p className="copy">
            It lists what the person must have and what they will be
            responsible for. Skills, years, duties. It almost never states
            what the role must produce — or what evidence would show that a
            particular candidate can produce it. Even the sophisticated
            versions measure the inputs to the job, not the outcome the job
            exists to create.
          </p>
          <p className="copy">
            So the honest answer to &ldquo;what are we measuring
            against&rdquo; is usually: a blurry picture that firms up as
            the meeting goes well.
          </p>
          <p className="copy">
            The fix is not a new technology. It is a discipline older than
            any of the tools: <b>define the standard before you measure.</b>{" "}
            No laboratory calibrates an instrument against a feeling. In
            hiring, that is the norm.
          </p>
          <p className="copy">
            So write it down — before anyone is searched, screened, or
            interviewed. What must this role produce? Which capabilities
            produce it? What separates a person who will succeed here from
            a person who merely qualifies?
          </p>
          <p className="copy">
            This is not an innovation. It is a recovery. The standard
            existed the day the role opened. Defining success first simply
            refuses to let it evaporate on the way to the interview.
          </p>
          <p className="pull-lg" style={{ maxWidth: "24ch" }}>
            If it is that simple, why does almost nobody do it?
          </p>
          <p className="copy">
            Because doing it properly is hard. It means working out what
            the business actually needs, which capabilities demonstrably
            deliver it, and what observable evidence would separate the
            genuinely capable from the well-rehearsed. It is hours of
            structured thinking per role. It is the first thing abandoned
            when a requisition is urgent. It is the work everyone agrees
            matters — and nobody has time to do.
          </p>
          <p className="ann-line">
            <span className="dot" aria-hidden="true" />
            It is also the work <em>Ann</em> was built to do first.
          </p>
          <p className="copy">
            Before any search begins, before any resume is read, before any
            interview is scheduled, Ann defines the standard and writes it
            down. The definition is called a RoleDNA — not a summary of the
            job description, but the standard the job description failed
            to preserve. Recovered.
          </p>
        </div>
      </section>

      {/* RoleDNA card */}
      <section className="fpb-roledna" aria-label="A RoleDNA example">
        <div className="fpb-col">
          <figure>
            <p className="tag">ROLEDNA — BUSINESS DEVELOPMENT SPECIALIST, EARLY-STAGE SAAS</p>
            <p className="subtag">(one of its Critical Success Factors, as delivered)</p>
            <p className="csf">
              <span className="n">CSF 02</span> — Discovery-led demo that
              converts interest into a committed next step.
            </p>
            <p className="csf-body">
              Runs a demo that begins with workflow discovery — the
              buyer&rsquo;s current screening and interview steps, the
              systems in use, the bottlenecks, the success metrics — and
              then shows only what maps to what was heard, earning a
              concrete next step rather than a polite ending.
            </p>
            <div className="cols">
              <div className="col strong">
                <p className="lbl">STRONG LOOKS LIKE</p>
                <p>
                  asks specific workflow questions before sharing a screen,
                  tailors the demo path, names what would make the product
                  a yes or a no, and closes with a scheduled next step and
                  a stakeholder plan.
                </p>
              </div>
              <div className="col">
                <p className="lbl">WEAK LOOKS LIKE</p>
                <p>
                  jumps straight into a feature tour, cannot connect the
                  product to the buyer&rsquo;s process, leaves the call
                  with &ldquo;we&rsquo;ll get back to you,&rdquo; and treats
                  the demo as the finish line.
                </p>
              </div>
            </div>
            <figcaption>
              <em>Anatomy of a RoleDNA:</em> Success Milestones &middot;
              Execution Capabilities &middot; Critical Success Factors
              &middot; Success Enablers &middot; Hiring Signals
            </figcaption>
          </figure>
        </div>
      </section>

      {/* continuation */}
      <section className="fpb-movement tight">
        <div className="fpb-col">
          <p className="copy">
            Read the two columns again. They are not requirements. Not
            competencies. Not keywords. They are descriptions of evidence —
            the observable difference between a person who has this
            capability and a person who merely talks like one.
          </p>
          <p className="copy">
            And notice the uncomfortable part. The weak column is not
            describing a bad candidate. It is often describing the
            impressive one.
          </p>
          <p className="pull-italic">
            Most people who see a RoleDNA for a role they know well say the
            same thing: I have hired for this role three times, and I have
            never once seen its success written down.
          </p>
          <p className="pull-lg" style={{ maxWidth: "22ch" }}>
            Every hiring process is exactly as good as its definition of
            success. No better.
          </p>
        </div>
      </section>

      {/* V · One Prediction */}
      <section className="fpb-movement" aria-labelledby="fpb-m5">
        <div className="fpb-col">
          <div className="fpb-head">
            <span className="num">V</span>
            <h2 id="fpb-m5">One Prediction</h2>
          </div>
          <p className="copy">Once the standard exists, everything downstream inherits it.</p>
          <p className="copy">
            Search stops being a keyword exercise. It becomes design —
            looking for people in whom evidence of this success could
            plausibly exist: adjacent companies, unfamiliar titles, places
            a keyword search cannot reach.
          </p>
          <p className="copy">
            Resumes are read for evidence, not impressions. Claims are
            weighed against the definition. Strengths are credited. Gaps
            are recorded, not glossed over.
          </p>
          <p className="copy">
            The interview becomes verification — structured, scored on
            evidence, asking for exactly the proof the standard demands.
          </p>
        </div>
      </section>

      {/* standalone italic pull */}
      <section className="fpb-pull-standalone">
        <div className="fpb-col">
          <p className="pull-italic">The same standard governs every decision.</p>
        </div>
      </section>

      {/* continuation */}
      <section className="fpb-movement tight">
        <div className="fpb-col">
          <p className="copy">
            And this matters more than any single stage. Each stage is no
            longer a ritual to complete. Each one gathers evidence for the
            same prediction — <em>this person will deliver these
            outcomes</em> — stated once, at the start, in the definition of
            success.
          </p>
          <p className="copy">
            Search gathers it. The resume evaluation weighs it. The
            interview tests it. The recommendation assembles it.
          </p>
        </div>
      </section>

      {/* FIG. 2 */}
      <figure className="fpb-fig2">
        <div className="fpb-col">
          <figcaption>Fig. 2 — Four stages, one prediction</figcaption>
          <div className="stages">
            {FIG2_STAGES.map((s) => (
              <div className="stage" key={s.n}>
                <span className="n">{s.n}</span>
                <p className="lbl">{s.label}</p>
                <p className="cap">{s.cap}</p>
              </div>
            ))}
          </div>
          <div className="down" aria-hidden="true">&#8595;</div>
          <div className="prediction">
            <p className="eyebrow">The prediction</p>
            <p>This person will deliver these outcomes.</p>
          </div>
        </div>
      </figure>

      {/* continuation */}
      <section className="fpb-movement tight">
        <div className="fpb-col">
          <p className="pull-lg">Not six stages. One prediction.</p>
          <p className="copy">
            That is the whole innovation — and it is a change of thinking
            before it is a change of tools. Which is why thirty years of
            faster tools never produced it.
          </p>
        </div>
      </section>

      {/* VI · Where Evidence Ends */}
      <section className="fpb-movement" aria-labelledby="fpb-m6">
        <div className="fpb-col">
          <div className="fpb-head">
            <span className="num">VI</span>
            <h2 id="fpb-m6">Where Evidence Ends</h2>
          </div>
          <p className="copy">
            What arrives at the end is a recommendation you can hold. A
            shortlist — not a longlist. And for each person on it: what the
            evidence says, where it came from, how strong it is, and what
            it does not cover.
          </p>
          <p className="copy">
            When a CFO asks why this candidate, the answer is not a score.
            It is the reasoning under the score — this claim, from this
            answer, weighed against this part of the standard. The
            confidence is earned. You can take it apart and check every
            piece.
          </p>
          <p className="copy">
            Confidence comes from evidence. It never works the other way
            around. A hiring process that starts from confidence and works
            backward to justification has already failed.
          </p>
          <p className="copy">
            And honesty requires saying the rest plainly: evidence is
            powerful, but evidence cannot see everything.
          </p>
          <p className="copy">
            No definition of success makes the future certain. A candidate
            with strong evidence can still fail. Circumstances change.
            Organisations change. People change.
          </p>
          <p className="copy">
            What evidence can do is different — and better than what it
            replaces. It can say how strong the prediction is, and show
            exactly what it rests on. Where the evidence is thin, the
            report says so. Where a capability could not be verified, the
            gap is admitted, not smoothed over. And Ann&rsquo;s confidence
            in her own evaluation is stated separately from the
            candidate&rsquo;s score — because those are two different
            things, and merging them is how numbers learn to lie.
          </p>
          <p className="pull-md" style={{ maxWidth: "26ch" }}>
            A prediction that knows its own limits is worth more than a
            certainty that doesn&rsquo;t.
          </p>
          <p className="copy">
            If a page like this one promised you certainty, you would be
            right not to trust the rest of it.
          </p>
        </div>
      </section>

      {/* VII · One Role · dark finale + CTA */}
      <section className="fpb-finale" aria-labelledby="fpb-m7">
        <div className="fpb-col">
          <div className="fpb-head">
            <span className="num">VII</span>
            <h2 id="fpb-m7">One Role</h2>
          </div>
          <p className="copy">
            Every hire you will ever make is a prediction. That was true
            before this page, and it will be true after it. The only
            question is what your predictions are built on.
          </p>
          <p className="copy">
            Before there was a vacancy, there was an outcome someone
            needed. Before your next one, there can be a standard.
          </p>
          <p className="copy">
            There is one way to see the difference, and it takes a day, not
            a meeting. Send Ann a live role — one you know well. Read what
            comes back.
          </p>
          <p className="copy">
            You will know within a page whether it is right. You have
            lived this role. You are the one reader it cannot bluff.
          </p>
          <p className="pull-xl">See what success looks like before hiring begins.</p>
          <div className="ctas">
            <Link to="/send-a-role" className="btn-cta">
              Send Ann a role <span className="arr">&#8594;</span>
            </Link>
            <button type="button" className="btn-share" onClick={share}>
              Share this argument <span className="arr">&#8599;</span>
            </button>
          </div>
        </div>
      </section>

      <button
        type="button"
        className="fpb-quiet-share"
        onClick={share}
        aria-label="Share this page"
      >
        Share <span className="arr">&#8599;</span>
      </button>

      {toast ? (
        <div className="fpb-toast" role="status">
          {toast}
        </div>
      ) : null}
    </div>
  );
}
